/**
 * Annotation overlay of the PDF viewer: an SVG stretched over the rendered
 * page canvas. All geometry is normalized (0..1), so the SVG uses a unit
 * viewBox and simply scales with zoom/DPI.
 *
 * Tools:
 *  - highlight → drag a translucent rectangle over text/figures
 *  - note      → click to drop a marker, type its text in the popover
 *  - ink       → freehand polyline drawing
 *  - select    → click an annotation to open/edit/delete it
 */
import { createSignal, For, Match, Show, Switch } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Trash2 } from "@fleet/icons";
import { generateId } from "@folio/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import {
  clampPoint,
  HIGHLIGHT_COLOR,
  INK_COLOR,
  INK_WIDTH,
  type NormPoint,
  NOTE_COLOR,
  type PdfAnnotation,
  rectFromDrag,
} from "../lib/pdf-annotations";

export type PdfTool = "select" | "highlight" | "note" | "ink";

const MIN_DRAG = 0.004; // ignore accidental micro-drags

interface PdfAnnotationLayerProps {
  /** Annotations of the CURRENT page only. */
  annotations: () => PdfAnnotation[];
  pageIndex: () => number;
  tool: () => PdfTool;
  onAdd: (annotation: PdfAnnotation) => void;
  onUpdateNote: (id: string, text: string) => void;
  onRemove: (id: string) => void;
}

interface DragState {
  start: NormPoint;
  current: NormPoint;
  points: NormPoint[];
}

export function PdfAnnotationLayer(props: PdfAnnotationLayerProps) {
  const { t } = useI18n();
  const [drag, setDrag] = createSignal<DragState | null>(null);
  /** Annotation whose popover (note editor / delete) is open. */
  const [activeId, setActiveId] = createSignal<string | null>(null);
  const [noteDraft, setNoteDraft] = createSignal("");
  let layerRef: SVGSVGElement | undefined;

  const active = () => props.annotations().find((a) => a.id === activeId()) ?? null;

  const toNorm = (event: PointerEvent): NormPoint => {
    const rect = layerRef?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) {
      return { x: 0, y: 0 };
    }
    return clampPoint({
      x: (event.clientX - rect.left) / rect.width,
      y: (event.clientY - rect.top) / rect.height,
    });
  };

  const openNote = (id: string, text: string) => {
    setActiveId(id);
    setNoteDraft(text);
  };

  const handlePointerDown = (event: PointerEvent) => {
    const tool = props.tool();
    const point = toNorm(event);
    if (tool === "note") {
      const id = generateId();
      props.onAdd({
        id,
        kind: "note",
        pageIndex: props.pageIndex(),
        at: point,
        text: "",
        color: NOTE_COLOR,
      });
      openNote(id, "");
      return;
    }
    if (tool === "highlight" || tool === "ink") {
      setActiveId(null);
      setDrag({ start: point, current: point, points: [point] });
      try {
        layerRef?.setPointerCapture(event.pointerId);
      } catch {
        // Pointer already released (e.g. synthetic events): dragging still
        // works while the pointer stays over the layer.
      }
    }
  };

  const handlePointerMove = (event: PointerEvent) => {
    const state = drag();
    if (!state) {
      return;
    }
    const point = toNorm(event);
    setDrag({ start: state.start, current: point, points: [...state.points, point] });
  };

  const handlePointerUp = () => {
    const state = drag();
    setDrag(null);
    if (!state) {
      return;
    }
    const tool = props.tool();
    if (tool === "highlight") {
      const rect = rectFromDrag(state.start, state.current);
      if (rect.w < MIN_DRAG && rect.h < MIN_DRAG) {
        return;
      }
      props.onAdd({
        id: generateId(),
        kind: "highlight",
        pageIndex: props.pageIndex(),
        rect,
        color: HIGHLIGHT_COLOR,
      });
    } else if (tool === "ink" && state.points.length > 1) {
      props.onAdd({
        id: generateId(),
        kind: "ink",
        pageIndex: props.pageIndex(),
        points: state.points,
        color: INK_COLOR,
        width: INK_WIDTH,
      });
    }
  };

  const handleAnnotationClick = (annotation: PdfAnnotation, event: MouseEvent) => {
    if (props.tool() !== "select") {
      return;
    }
    event.stopPropagation();
    if (annotation.kind === "note") {
      openNote(annotation.id, annotation.text);
    } else {
      setActiveId(annotation.id === activeId() ? null : annotation.id);
    }
  };

  const commitNote = () => {
    const current = active();
    if (current?.kind === "note") {
      props.onUpdateNote(current.id, noteDraft());
    }
    setActiveId(null);
  };

  const popoverPosition = () => {
    const current = active();
    if (!current) {
      return { left: "50%", top: "50%" };
    }
    const at =
      current.kind === "note"
        ? current.at
        : current.kind === "highlight"
          ? { x: current.rect.x + current.rect.w / 2, y: current.rect.y + current.rect.h }
          : (current.points[0] ?? { x: 0.5, y: 0.5 });
    return { left: `${Math.min(at.x, 0.7) * 100}%`, top: `${Math.min(at.y + 0.02, 0.85) * 100}%` };
  };

  return (
    <div class="absolute inset-0" data-testid="pdf-annotation-layer">
      <svg
        ref={(el) => (layerRef = el)}
        viewBox="0 0 1 1"
        preserveAspectRatio="none"
        class="h-full w-full touch-none"
        classList={{ "cursor-crosshair": props.tool() !== "select" }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={() => setDrag(null)}
      >
        <For each={props.annotations()}>
          {(annotation) => (
            <Switch>
              <Match when={annotation.kind === "highlight" && annotation}>
                {(hl) => (
                  <rect
                    x={hl().rect.x}
                    y={hl().rect.y}
                    width={hl().rect.w}
                    height={hl().rect.h}
                    fill={hl().color}
                    fill-opacity="0.35"
                    data-testid="pdf-annotation"
                    data-kind="highlight"
                    data-annotation-id={hl().id}
                    class="pointer-events-auto"
                    style={{ cursor: props.tool() === "select" ? "pointer" : undefined }}
                    onClick={(e) => handleAnnotationClick(hl(), e)}
                  />
                )}
              </Match>
              <Match when={annotation.kind === "ink" && annotation}>
                {(ink) => (
                  <polyline
                    points={ink()
                      .points.map((p) => `${p.x},${p.y}`)
                      .join(" ")}
                    fill="none"
                    stroke={ink().color}
                    stroke-width={ink().width}
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-opacity="0.9"
                    data-testid="pdf-annotation"
                    data-kind="ink"
                    data-annotation-id={ink().id}
                    class="pointer-events-auto"
                    style={{ cursor: props.tool() === "select" ? "pointer" : undefined }}
                    onClick={(e) => handleAnnotationClick(ink(), e)}
                  />
                )}
              </Match>
              <Match when={annotation.kind === "note" && annotation}>
                {(note) => (
                  <g
                    data-testid="pdf-annotation"
                    data-kind="note"
                    data-annotation-id={note().id}
                    class="pointer-events-auto"
                    style={{ cursor: "pointer" }}
                    onClick={(e) => handleAnnotationClick(note(), e)}
                  >
                    <rect
                      x={note().at.x}
                      y={note().at.y}
                      width="0.018"
                      height="0.018"
                      rx="0.004"
                      fill={note().color}
                    />
                    <text
                      x={note().at.x + 0.009}
                      y={note().at.y + 0.014}
                      font-size="0.014"
                      text-anchor="middle"
                      fill="white"
                      style={{ "user-select": "none" }}
                    >
                      ✎
                    </text>
                  </g>
                )}
              </Match>
            </Switch>
          )}
        </For>

        {/* Draft feedback while dragging */}
        <Show when={drag()}>
          {(state) => (
            <Show
              when={props.tool() === "highlight"}
              fallback={
                <polyline
                  points={state()
                    .points.map((p) => `${p.x},${p.y}`)
                    .join(" ")}
                  fill="none"
                  stroke={INK_COLOR}
                  stroke-width={INK_WIDTH}
                  stroke-opacity="0.6"
                />
              }
            >
              <rect
                x={rectFromDrag(state().start, state().current).x}
                y={rectFromDrag(state().start, state().current).y}
                width={rectFromDrag(state().start, state().current).w}
                height={rectFromDrag(state().start, state().current).h}
                fill={HIGHLIGHT_COLOR}
                fill-opacity="0.25"
                stroke={HIGHLIGHT_COLOR}
                stroke-width="0.002"
              />
            </Show>
          )}
        </Show>
      </svg>

      {/* ── Annotation popover (note editor / delete) ─────────────── */}
      <Show when={active()}>
        {(annotation) => (
          <div
            class="absolute z-10 w-56 rounded-lg border border-border/70 bg-card p-2 shadow-xl"
            style={popoverPosition()}
            data-testid="pdf-annotation-popover"
          >
            <Show when={annotation().kind === "note"}>
              <textarea
                value={noteDraft()}
                onInput={(e) => setNoteDraft(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    commitNote();
                  }
                }}
                placeholder={t(msg`Write a note…`)}
                rows={3}
                class="w-full resize-none rounded-md border border-border/60 bg-background px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-accent"
                data-testid="pdf-note-input"
              />
            </Show>
            <div class="mt-1.5 flex items-center justify-between gap-2">
              <Button
                variant="ghost"
                size="sm"
                class="gap-1 text-muted-foreground hover:text-danger"
                data-testid="pdf-annotation-delete"
                onClick={() => {
                  props.onRemove(annotation().id);
                  setActiveId(null);
                }}
              >
                <Trash2 class="h-3.5 w-3.5" />
                {t(msg`Delete`)}
              </Button>
              <Show
                when={annotation().kind === "note"}
                fallback={
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="pdf-annotation-close"
                    onClick={() => setActiveId(null)}
                  >
                    {t(msg`Close`)}
                  </Button>
                }
              >
                <Button size="sm" data-testid="pdf-note-save" onClick={commitNote}>
                  {t(msg`Save`)}
                </Button>
              </Show>
            </div>
          </div>
        )}
      </Show>
    </div>
  );
}
